import { API } from '@/config';
import type {
  AddNoteRequest,
  CreatePatientLinkRequest,
  PatientAnswerRequest,
  PatientLinkResponse,
  PatientOpenResponse,
  DecisionRequest,
  DecisionResponse,
  ReviewCase,
  ShareOpenResponse,
} from '@/types';
import { api } from './client';

/**
 * The four things a dentist can do with a link.
 *
 * The token is a parameter on every call rather than state in this module. It
 * comes from the URL, and keeping it a parameter means there is no cached value
 * for a later call to pick up after the dentist has navigated away.
 */
export const reviewService = {
  /**
   * Opens the link.
   *
   * POST despite being a read, because the passcode travels in the body - in a
   * query string it would end up in browser history, the referrer header and
   * every log between here and the server.
   */
  open(token: string, passcode?: string, signal?: AbortSignal): Promise<ShareOpenResponse> {
    return api.post<ShareOpenResponse>(API.endpoints.open, passcode ? { passcode } : {}, {
      token,
      ...(signal ? { signal } : {}),
    });
  },

  /** The case's 3D PDF as a signed link (an hour), or 404 when the lab has made none. */
  report(
    token: string,
    signal?: AbortSignal,
  ): Promise<{ url: string; sizeBytes: number | null; exportedAt: string }> {
    return api.get(API.endpoints.report, { token, ...(signal ? { signal } : {}) });
  },

  case(token: string, signal?: AbortSignal): Promise<ReviewCase> {
    return api.get<ReviewCase>(API.endpoints.case, { token, ...(signal ? { signal } : {}) });
  },

  addNote(token: string, body: AddNoteRequest): Promise<{ id: string; createdAt: string }> {
    return api.post<{ id: string; createdAt: string }>(API.endpoints.notes, body, { token });
  },

  decide(token: string, body: DecisionRequest): Promise<DecisionResponse> {
    return api.post<DecisionResponse>(API.endpoints.decision, body, { token });
  },

  /** The dentist's patient link (dentist token). */
  patientLink(token: string, signal?: AbortSignal): Promise<PatientLinkResponse> {
    return api.get<PatientLinkResponse>(API.endpoints.patientLink, {
      token,
      ...(signal ? { signal } : {}),
    });
  },

  createPatientLink(token: string, body: CreatePatientLinkRequest): Promise<PatientLinkResponse> {
    return api.post<PatientLinkResponse>(API.endpoints.patientLink, body, { token });
  },

  turnOffPatientLink(token: string): Promise<PatientLinkResponse> {
    return api.post<PatientLinkResponse>(API.endpoints.patientLinkOff, {}, { token });
  },

  /** The patient's own page (patient token). */
  openPatient(token: string, signal?: AbortSignal): Promise<PatientOpenResponse> {
    return api.post<PatientOpenResponse>(
      API.endpoints.patientOpen,
      {},
      { token, ...(signal ? { signal } : {}) },
    );
  },

  answerPatient(token: string, body: PatientAnswerRequest): Promise<PatientOpenResponse> {
    return api.post<PatientOpenResponse>(API.endpoints.patientAnswer, body, { token });
  },
};
